import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (img) => {
  if (!img) return "";
  if (img.startsWith("http")) return img;
  if (img.startsWith("/api/")) return `${BACKEND_URL}${img}`;
  return img;
};

const isInternal = (link) => link && link.startsWith("/") && !link.startsWith("//");

const Cta = ({ slide }) => {
  if (!slide.button_text) return null;
  const cls =
    "inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold shadow-lg hover:brightness-110 transition-all duration-300 text-sm md:text-base";
  const style = { backgroundColor: slide.button_color || "#C9A13A", boxShadow: `0 10px 25px ${(slide.button_color || "#C9A13A")}40` };
  const inner = (
    <>
      {slide.button_text} <ArrowRight size={16} />
    </>
  );
  if (isInternal(slide.button_link)) {
    return (
      <Link to={slide.button_link} className={cls} style={style} data-testid="promo-cta">
        {inner}
      </Link>
    );
  }
  return (
    <a href={slide.button_link || "#"} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid="promo-cta">
      {inner}
    </a>
  );
};

const VideoMedia = ({ slide, fit }) => (
  <video
    src={resolveImg(slide.video)}
    className={`w-full h-full ${fit}`}
    autoPlay={slide.video_autoplay !== false}
    loop={slide.video_loop !== false}
    muted={slide.video_muted !== false || slide.video_autoplay !== false}
    controls={!!slide.video_controls}
    playsInline
    preload="metadata"
    data-testid="promo-video"
  />
);

const FullBannerSlide = ({ slide }) => {
  const content = (
    <div className="relative w-full h-full bg-gradient-to-br from-[#FBF3DE] to-[#F3E4BC] flex items-center justify-center">
      {slide.media_type === "video" && slide.video ? (
        <VideoMedia slide={slide} fit="object-contain" />
      ) : (
        <img src={resolveImg(slide.image)} alt={slide.title} className="w-full h-full object-contain" />
      )}
    </div>
  );
  if (isInternal(slide.button_link)) {
    return <Link to={slide.button_link} className="block w-full h-full" data-testid="promo-cta">{content}</Link>;
  }
  if (slide.button_link) {
    return <a href={slide.button_link} target="_blank" rel="noopener noreferrer" className="block w-full h-full" data-testid="promo-cta">{content}</a>;
  }
  return content;
};

const Slide = ({ slide }) => {
  if (slide.full_image) return <FullBannerSlide slide={slide} />;
  return (
  <div className="grid grid-cols-1 md:grid-cols-2 h-full">
    {/* Media */}
    <div className="relative h-44 sm:h-56 md:h-full overflow-hidden bg-gradient-to-b from-[#0b1436] to-[#080b18]">
      {slide.media_type === "video" && slide.video ? (
        <VideoMedia slide={slide} fit="object-cover" />
      ) : (
        slide.image && <img src={resolveImg(slide.image)} alt={slide.title} className="w-full h-full object-cover" />
      )}
      {slide.badge && (
        <span className="absolute top-4 left-4 inline-flex items-center px-3.5 py-1.5 rounded-full bg-gold text-[#2C1A0E] text-xs font-bold uppercase tracking-wider shadow-md">
          {slide.badge}
        </span>
      )}
    </div>
    {/* Content */}
    <div className="flex flex-col justify-center px-6 py-6 md:px-12 md:py-10 bg-gradient-to-br from-[#FBF3DE] to-[#F3E4BC]">
      {slide.subtitle && (
        <p className="text-xs uppercase tracking-[0.28em] text-gold font-semibold mb-3">{slide.subtitle}</p>
      )}
      <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium text-brown-black leading-tight tracking-tight">
        {slide.title}
      </h3>
      {slide.description && (
        <p className="mt-3 md:mt-4 text-sm md:text-base text-brown-black/70 leading-relaxed line-clamp-3 md:line-clamp-none">
          {slide.description}
        </p>
      )}
      <div className="mt-5 md:mt-7">
        <Cta slide={slide} />
      </div>
    </div>
  </div>
  );
};

export const PromoCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [settings, setSettings] = useState({
    autoplay: true, autoplay_speed: 5000, transition: "fade",
    infinite: true, arrows: true, dots: true, pause_on_hover: true, swipe: true, show_on_mobile: true,
  });
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);

  useEffect(() => {
    const bust = `?t=${Date.now()}`;
    api.get(`/carousel/slides${bust}`).then((r) => setSlides(r.data || [])).catch(() => {});
    api.get(`/carousel/settings${bust}`).then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  const count = slides.length;

  const goTo = useCallback((i) => {
    if (count === 0) return;
    if (settings.infinite) setCurrent((i + count) % count);
    else setCurrent(Math.max(0, Math.min(count - 1, i)));
  }, [count, settings.infinite]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (!settings.autoplay || paused || count <= 1) return;
    const id = setInterval(() => {
      setCurrent((c) => (settings.infinite ? (c + 1) % count : Math.min(count - 1, c + 1)));
    }, Math.max(2000, settings.autoplay_speed || 5000));
    return () => clearInterval(id);
  }, [settings.autoplay, settings.autoplay_speed, settings.infinite, paused, count]);

  if (count === 0) return null;

  const onTouchStart = (e) => { if (settings.swipe) touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (!settings.swipe || touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next() : prev());
    touchX.current = null;
  };

  const slideStyle = (i) => {
    const t = settings.transition;
    if (t === "slide") {
      return { transform: `translateX(${(i - current) * 100}%)`, opacity: i === current ? 1 : 0, transition: "transform 0.7s ease, opacity 0.7s ease" };
    }
    if (t === "zoom") {
      return { opacity: i === current ? 1 : 0, transform: i === current ? "scale(1)" : "scale(1.06)", transition: "opacity 0.8s ease, transform 0.8s ease" };
    }
    return { opacity: i === current ? 1 : 0, transition: "opacity 0.8s ease" };
  };

  return (
    <section className={`bg-[#FBF6E9] pt-8 pb-4 md:pt-10 ${settings.show_on_mobile === false ? "hidden md:block" : ""}`} data-testid="promo-carousel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="relative rounded-2xl overflow-hidden border border-gold/40 shadow-xl shadow-brown-dark/10 bg-[#FBF3DE] h-[500px] sm:h-[460px] md:h-[440px]"
          onMouseEnter={() => settings.pause_on_hover && setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {slides.map((s, i) => (
            <div key={s.id} className="absolute inset-0" style={slideStyle(i)} aria-hidden={i !== current} data-testid={`promo-slide-${i}`}>
              <Slide slide={s} />
            </div>
          ))}

          {/* Arrows */}
          {settings.arrows && count > 1 && (
            <>
              <button onClick={prev} aria-label="Précédent" data-testid="promo-prev"
                className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 rounded-full bg-white/85 border border-gold/40 text-brown-dark flex items-center justify-center shadow-md hover:bg-gold hover:text-white transition-colors">
                <ChevronLeft size={20} />
              </button>
              <button onClick={next} aria-label="Suivant" data-testid="promo-next"
                className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 rounded-full bg-white/85 border border-gold/40 text-brown-dark flex items-center justify-center shadow-md hover:bg-gold hover:text-white transition-colors">
                <ChevronRight size={20} />
              </button>
            </>
          )}

          {/* Dots */}
          {settings.dots && count > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2" data-testid="promo-dots">
              {slides.map((s, i) => (
                <button key={s.id} onClick={() => goTo(i)} aria-label={`Aller à la diapositive ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === current ? "w-7 bg-gold" : "w-2 bg-brown-dark/25 hover:bg-brown-dark/40"}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PromoCarousel;
